/**
 * Light, safe markdown for brief section lines (bold / italic only).
 * Escapes HTML first so AI output cannot inject markup.
 */
export function formatBriefLine(raw: string): string {
  if (!raw) return '';
  let s = String(raw);
  s = s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  // Bold first, then single-asterisk / underscore italic on the remainder
  s = s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/__(.+?)__/g, '<strong>$1</strong>');
  s = s.replace(/\*(.+?)\*/g, '<em>$1</em>');
  s = s.replace(/_([^_\s][^_]*?)_/g, '<em>$1</em>');
  s = linkify(s);
  // Leftover decorative asterisks from failed bold
  s = s.replace(/\*{1,2}/g, '');
  return s;
}

/**
 * Block markdown for the live generation stream: headings, lists, paragraphs, bold, italic.
 * Text is left raw; callers must pass the result through sanitizeHtml.
 */
export function formatBriefMarkdown(raw: string): string {
  if (!raw) return '';
  const lines = String(raw).replace(/\r\n/g, '\n').split('\n');
  const blocks: string[] = [];
  let para: string[] = [];
  let list: string[] = [];
  let listTag: 'ul' | 'ol' | null = null;

  const flushPara = () => {
    if (para.length === 0) return;
    blocks.push('<p>' + para.map(inlineMarkdown).join('<br>') + '</p>');
    para = [];
  };
  const flushList = () => {
    if (list.length === 0 || !listTag) return;
    blocks.push(
      '<' + listTag + '>' + list.map((item) => '<li>' + inlineMarkdown(item) + '</li>').join('') + '</' + listTag + '>'
    );
    list = [];
    listTag = null;
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      flushPara();
      flushList();
      continue;
    }
    const heading = trimmed.match(/^(#{1,4})\s+(.+)$/);
    if (heading) {
      flushPara();
      flushList();
      const tag = heading[1].length <= 2 ? 'h2' : 'h3';
      blocks.push('<' + tag + '>' + inlineMarkdown(heading[2]) + '</' + tag + '>');
      continue;
    }
    const bullet = trimmed.match(/^[-*]\s+(.+)$/);
    if (bullet) {
      flushPara();
      if (listTag && listTag !== 'ul') flushList();
      listTag = 'ul';
      list.push(bullet[1]);
      continue;
    }
    const numbered = trimmed.match(/^\d+[.)]\s+(.+)$/);
    if (numbered) {
      flushPara();
      if (listTag && listTag !== 'ol') flushList();
      listTag = 'ol';
      list.push(numbered[1]);
      continue;
    }
    flushList();
    para.push(trimmed);
  }
  flushPara();
  flushList();
  return blocks.join('');
}

function inlineMarkdown(text: string): string {
  return linkify(text
    .replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>')
    .replace(/__([^_\n]+)__/g, '<strong>$1</strong>')
    .replace(/\*([^*\n]+)\*/g, '<em>$1</em>'));
}

function linkify(value: string): string {
  let s = value.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, (_match, label, href) => {
    return `<a href="${href}">${label}</a>`;
  });
  s = s.replace(/(?<!href=")(https?:\/\/[^\s<]+)/g, (url) => {
    const clean = url.replace(/[.,)]+$/, '');
    return `<a href="${clean}">${clean}</a>${url.slice(clean.length)}`;
  });
  return s;
}

export interface TimingRow {
  phase: string;
  activity: string;
}

/** Best-effort client-side parse when older briefs lack section.timeline */
export function parseTimingLines(lines: string[]): TimingRow[] {
  const rows: TimingRow[] = [];
  for (const raw of lines) {
    const line = (raw || '').trim();
    if (!line) continue;
    let m = line.match(/^Phase:\s*(.+?)\s*\|\s*Activity:\s*(.+)$/i);
    if (m) {
      rows.push({ phase: m[1].trim(), activity: m[2].trim() });
      continue;
    }
    m = line.match(/^\*{0,2}(.+?)\*{0,2}\s*[:—–-]\s+(.+)$/);
    if (m) {
      const phase = m[1].replace(/\*/g, '').trim();
      const activity = m[2].trim();
      if (phase && activity && !/^phase$/i.test(phase)) {
        rows.push({ phase, activity });
      }
    }
  }
  return rows;
}
